import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceMetrics = ({ agent }) => {
  const metrics = [
    {
      title: 'Monthly Volume',
      value: `$${agent?.performance?.monthlyVolume?.toLocaleString()}`,
      subtitle: `of $${agent?.performance?.monthlyTarget?.toLocaleString()} target`,
      icon: 'TrendingUp',
      progress: {
        percentage: (agent?.performance?.monthlyVolume / agent?.performance?.monthlyTarget) * 100,
        color: 'bg-primary'
      },
      change: '+12.5%',
      changeType: 'positive'
    },
    {
      title: 'Commission Earned',
      value: `$${agent?.commissions?.thisMonth?.toLocaleString()}`,
      subtitle: 'This month',
      icon: 'DollarSign',
      change: `+$${(agent?.commissions?.thisMonth - agent?.commissions?.lastMonth)?.toFixed(2)}`,
      changeType: agent?.commissions?.thisMonth > agent?.commissions?.lastMonth ? 'positive' : 'negative',
      actionLabel: 'View Details',
      onAction: () => console.log('View commission details')
    },
    {
      title: 'Customer Acquisition',
      value: agent?.performance?.customersAcquired,
      subtitle: `of ${agent?.performance?.customerTarget} monthly target`,
      icon: 'Users',
      progress: {
        percentage: (agent?.performance?.customersAcquired / agent?.performance?.customerTarget) * 100,
        color: 'bg-success'
      },
      change: '+8 new',
      changeType: 'positive'
    },
    {
      title: 'Success Rate',
      value: `${agent?.performance?.successRate}%`,
      subtitle: 'Transaction success rate',
      icon: 'CheckCircle',
      change: '+0.8%',
      changeType: 'positive',
      badge: agent?.performance?.successRate >= 95 ? 'Excellence' : 
             agent?.performance?.successRate >= 90 ? 'Good' : 'Needs Improvement',
      badgeColor: agent?.performance?.successRate >= 95 ? 'bg-success/10 text-success' :
                  agent?.performance?.successRate >= 90 ? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Performance Overview</h2>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Calendar" size={16} />
          <span>Current Month</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics?.map((metric, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={metric?.icon} size={20} className="text-primary" />
                </div>
                {metric?.badge && (
                  <span className={`px-2 py-1 text-xs rounded-full ${metric?.badgeColor}`}>
                    {metric?.badge}
                  </span>
                )}
              </div>
            </div>

            {/* Metric Value */}
            <div className="space-y-2 mb-4">
              <h3 className="text-sm font-medium text-muted-foreground">{metric?.title}</h3>
              <p className="text-2xl font-bold text-foreground">{metric?.value}</p>
              {metric?.subtitle && (
                <p className="text-sm text-muted-foreground">{metric?.subtitle}</p>
              )}
            </div>

            {/* Progress Bar */}
            {metric?.progress && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">{Math.round(metric?.progress?.percentage)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${metric?.progress?.color}`}
                    style={{ width: `${Math.min(metric?.progress?.percentage, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Change Indicator & Action */}
            <div className="flex items-center justify-between">
              {metric?.change && (
                <div className={`flex items-center space-x-1 text-sm ${
                  metric?.changeType === 'positive' ? 'text-success' : 'text-error'
                }`}>
                  <Icon 
                    name={metric?.changeType === 'positive' ? 'TrendingUp' : 'TrendingDown'} 
                    size={16} 
                  />
                  <span>{metric?.change}</span>
                </div>
              )}

              {metric?.actionLabel && (
                <button
                  onClick={metric?.onAction}
                  className="text-sm text-primary hover:text-primary/80 font-medium"
                >
                  {metric?.actionLabel}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Performance Summary */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Monthly Summary</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Avg Transaction Value:</span>
            <span className="font-medium text-foreground">
              ${agent?.performance?.avgTransactionValue?.toLocaleString()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary mb-1">
              {Math.round((agent?.performance?.monthlyVolume / agent?.performance?.monthlyTarget) * 100)}%
            </div>
            <p className="text-sm text-muted-foreground">Volume Target Achievement</p>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-success mb-1">
              {Math.round((agent?.performance?.customersAcquired / agent?.performance?.customerTarget) * 100)}%
            </div>
            <p className="text-sm text-muted-foreground">Customer Target Achievement</p>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-foreground mb-1">
              ${agent?.commissions?.thisMonth?.toLocaleString()}
            </div>
            <p className="text-sm text-muted-foreground">Total Commission Earned</p>
          </div>
        </div>

        {/* Performance Status */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon 
                name="Award" 
                size={20} 
                className={
                  agent?.performance?.monthlyVolume >= agent?.performance?.monthlyTarget * 0.9 
                    ? 'text-success' :'text-warning'
                } 
              />
              <span className="font-medium text-foreground">
                {agent?.performance?.monthlyVolume >= agent?.performance?.monthlyTarget 
                  ? 'Target Exceeded' 
                  : agent?.performance?.monthlyVolume >= agent?.performance?.monthlyTarget * 0.9
                  ? 'On Track' :'Needs Attention'
                }
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              {Math.ceil((new Date()?.getMonth() === new Date()?.getMonth()) ? 
                (30 - new Date()?.getDate()) : 0)} days remaining
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;